/**
 * Opportunities List Screen - MOBILE ONLY
 * 
 * View opportunities within a selected portfolio.
 * Navigates to Opportunity Detail on tap.
 * 
 * Terminology Update:
 * - "Property" → "Opportunity" (tracking a potential purchase)
 * - "research" → "evaluating"
 * - "listed_for_sale" → "selling"
 * - Added "passed" status (soft archive)
 */

import React, { useCallback, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, RefreshControl } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, icon } from '../../../shared/tokens/mobile';
import { Opportunity } from '../../../shared/types/opportunity';
import { logger } from '../../../shared/utils/logger';
import { usePortfolioOpportunitiesWithFilter } from '../../../hooks/usePortfolioOpportunitiesWithFilter';
import {
  ScreenContainer,
  ScreenHeader,
  ListCard,
  OpportunityCard,
  OpportunityFilterChips,
} from '../../../components/v2/mobile';
import { AddOpportunityModal } from '../../../components/v2/opportunity';
import { styles } from '../../../components/v2/opportunity/opportunitiesListStyles';

// =============================================================================
// SCREEN
// =============================================================================

export default function OpportunitiesListScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ id: string }>();
  const rawId = params.id;
  
  // Handle potential array or undefined values from useLocalSearchParams
  const idString = Array.isArray(rawId) ? rawId[0] : rawId;
  const portfolioId = idString ? parseInt(idString, 10) : null;
  
  const {
    portfolio,
    filteredOpportunities,
    filterCounts,
    filter,
    setFilter,
    loading,
    refreshing,
    refresh,
    error,
  } = usePortfolioOpportunitiesWithFilter(portfolioId);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);

  const handleBack = useCallback(() => router.back(), [router]);

  const handleOpportunityPress = useCallback((opportunity: Opportunity) => {
    router.push({
      pathname: '/v2/property/[id]',
      params: { id: opportunity.id.toString(), portfolioId: portfolioId?.toString() },
    });
  }, [router, portfolioId]);

  const handleAddOpportunity = useCallback(() => {
    logger.debug('Add opportunity requested', 'opportunities', { portfolioId });
    setShowAddModal(true);
  }, [portfolioId]);

  const handleAddSuccess = useCallback((opportunity: Opportunity) => {
    logger.info('Opportunity created', 'opportunities', { id: opportunity.id });
    refresh();
    router.push({
      pathname: '/v2/property/[id]',
      params: { id: opportunity.id.toString(), portfolioId: portfolioId?.toString() },
    });
  }, [refresh, router, portfolioId]);

  // Loading state
  if (loading) {
    return (
      <ScreenContainer>
        <ScreenHeader title="" onBack={handleBack} loading />
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      </ScreenContainer>
    );
  }

  // Error state
  if (error || !portfolio) {
    return (
      <ScreenContainer>
        <ScreenHeader title="Portfolio" onBack={handleBack} />
        <View style={styles.centerContainer}>
          <Ionicons name={error ? "cloud-offline-outline" : "alert-circle-outline"} size={icon.xxl} color={color.error} />
          <Text style={styles.errorText}>{error ? 'Unable to load portfolio' : 'Portfolio not found'}</Text>
          <Text style={styles.errorSubtext}>{error ? 'Check your connection and try again' : `Portfolio ID: ${portfolioId}`}</Text>
          {error && (
            <Pressable onPress={refresh} style={styles.retryButton}>
              <Ionicons name="refresh-outline" size={icon.sm} color={color.surface} />
              <Text style={styles.retryButtonText}>Retry</Text>
            </Pressable>
          )}
        </View>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      <ScreenHeader title={portfolio.name} onBack={handleBack} />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} tintColor={color.primary} />}
      >
        <OpportunityFilterChips activeFilter={filter} onFilterChange={setFilter} counts={filterCounts} />

        <View style={styles.section}>
          {filteredOpportunities.length === 0 ? (
            <EmptyState hasFilter={filter !== 'all'} onAdd={handleAddOpportunity} />
          ) : (
            <ListCard>
              {filteredOpportunities.map((opportunity, index) => (
                <OpportunityCard
                  key={opportunity.id}
                  name={opportunity.name || opportunity.street_address || opportunity.address || 'Unnamed'}
                  location={formatLocation(opportunity)}
                  acreage={opportunity.acreage}
                  askingPrice={opportunity.asking_price}
                  status={opportunity.status}
                  onPress={() => handleOpportunityPress(opportunity)}
                  hideBorder={index === filteredOpportunities.length - 1}
                />
              ))}
            </ListCard>
          )}
        </View>

        <View style={styles.section}>
          <AddButton onPress={handleAddOpportunity} />
        </View>
      </ScrollView>

      {portfolioId && (
        <AddOpportunityModal
          visible={showAddModal}
          portfolioId={portfolioId}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
        />
      )}
    </ScreenContainer>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function EmptyState({ hasFilter, onAdd }: { hasFilter: boolean; onAdd?: () => void }) {
  return (
    <ListCard>
      <View style={styles.emptyState}>
        <Ionicons name="search-outline" size={icon.xxl} color={color.textMuted} />
        <Text style={styles.emptyTitle}>{hasFilter ? 'No matching opportunities' : 'No opportunities yet'}</Text>
        <Text style={styles.emptySubtitle}>{hasFilter ? 'Try a different filter' : 'Add a listing to start tracking'}</Text>
        {!hasFilter && onAdd && (
          <Pressable onPress={onAdd} style={styles.emptyAddButton}>
            <Ionicons name="add" size={icon.sm} color={color.surface} />
            <Text style={styles.emptyAddButtonText}>Add Your First</Text>
          </Pressable>
        )}
      </View>
    </ListCard>
  );
}

function AddButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.addButton, pressed && styles.addButtonPressed]}
      accessibilityRole="button"
      accessibilityLabel="Add opportunity"
    >
      <Ionicons name="add-circle-outline" size={icon.md} color={color.primary} />
      <Text style={styles.addButtonText}>Add Opportunity</Text>
    </Pressable>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

function formatLocation(opportunity: Opportunity): string {
  const parts: string[] = [];
  if (opportunity.city) parts.push(opportunity.city);
  if (opportunity.state) parts.push(opportunity.state);
  if (opportunity.county) parts.push(`${opportunity.county} County`);
  return parts.join(', ');
}
